import type { ScpData } from "@hobbyshopify/backend-client";
import { useEffect, useMemo, useRef, useState } from "react";
import { GRADE_CATEGORIES, type Grade, gradePriceText, RAW_GRADE } from "../core/primitives/grades";

/**
 * The grade field: type it, or pick it from the graded list with prices.
 *
 * Both halves are needed. The stored value is **free-form text** — the model
 * writes "PSA 10", "psa10", "9.5", "SGC 98", and scan-core's `normalizeGrade`
 * parses whatever it finds — so a plain `<select>` would refuse readings the
 * pipeline produces every day, and a list can never cover the long tail of
 * grader names. But typing a grade blind is the wrong way to make the decision
 * this screen is actually about: the grade is part of the card's identity and
 * it picks the comp the price comes from, so the reviewer is choosing a price
 * as much as a label. The list carries the SportsCardsPro figure next to each
 * grade for that reason — the same numbers the eBay grid below shows, from the
 * same payload.
 */

type Option = { grade: Grade; group: string };

const OPTIONS: Option[] = [
  { grade: RAW_GRADE, group: "Ungraded" },
  ...GRADE_CATEGORIES.flatMap((category) =>
    category.grades.map((grade) => ({ grade, group: category.id.toUpperCase() })),
  ),
];

/** "psa1" matches "PSA 1" and "PSA 10": spacing is noise in a hurry. */
const squash = (text: string) => text.toLowerCase().replace(/\s+/g, "");

export function GradeCombobox({
  value,
  onType,
  onCommit,
  onPick,
  scpData,
  placeholder,
}: {
  value: string;
  onType: (grade: string) => void;
  /**
   * A typed grade the reviewer has finished with — fired on blur, not per
   * keystroke. Re-pricing on every character would quote "PSA 1" on the way to
   * "PSA 10", and half of those figures are an order of magnitude out.
   */
  onCommit?: (grade: string) => void;
  onPick: (grade: Grade) => void;
  scpData?: ScpData | null;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [activeRaw, setActive] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);
  // Typed-since-last-committed. A pick answers for itself through `onPick`, so
  // the blur that follows it must not re-announce the same change.
  const typed = useRef(false);

  // A value that is itself one of the options is a chosen grade, not a query —
  // filtering on it would leave a list of one and hide its neighbours, which
  // are exactly what someone reopening the list wants to compare against.
  const options = useMemo(() => {
    const query = squash(value);
    const exact = OPTIONS.some((o) => squash(o.grade.label) === query);
    if (!query || exact) return OPTIONS;
    return OPTIONS.filter((o) => squash(o.grade.label).includes(query));
  }, [value]);

  // A shrinking filter can leave the highlight past the end; clamping beats an
  // effect that resets it, which would fight the arrow keys on every render.
  const active = Math.min(activeRaw, options.length - 1);

  useEffect(() => {
    if (open) listRef.current?.children[active]?.scrollIntoView({ block: "nearest" });
  }, [open, active]);

  const pick = (option: Option) => {
    typed.current = false;
    onPick(option.grade);
    setOpen(false);
  };

  const commit = () => {
    if (!typed.current) return;
    typed.current = false;
    onCommit?.(value);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") return setOpen(false);
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) return setOpen(true);
      const step = e.key === "ArrowDown" ? 1 : -1;
      return setActive((i) => (i + step + options.length) % options.length);
    }
    // Enter only commits a highlighted option while the list is open; otherwise
    // it belongs to the form, and stealing it would swallow a typed grade.
    if (e.key === "Enter") {
      if (open && options[active]) {
        e.preventDefault();
        pick(options[active]);
      } else {
        commit();
      }
    }
  };

  return (
    <div className="relative">
      <input
        className="field pr-9"
        value={value}
        onChange={(e) => {
          typed.current = true;
          onType(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        // Every control in the list preserves the input's focus on mousedown, so
        // losing focus really does mean the reviewer has moved on.
        onBlur={() => {
          setOpen(false);
          commit();
        }}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-controls="grade-options"
      />
      <button
        type="button"
        tabIndex={-1}
        aria-label={open ? "Hide grades" : "Show grades"}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((v) => !v)}
        className="absolute inset-y-0 right-0 flex w-9 items-center justify-center text-faint hover:text-white"
      >
        {open ? "▴" : "▾"}
      </button>

      {open && options.length > 0 && (
        <ul
          id="grade-options"
          ref={listRef}
          className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-btn border border-line bg-app-elevated py-1 shadow-lg"
        >
          {options.map((option, i) => {
            const priceText = gradePriceText(option.grade.key, scpData);
            // The group only earns a line when it changes: a header above every
            // row would double the length of a list read by scanning.
            const header = i === 0 || options[i - 1].group !== option.group ? option.group : null;
            return (
              <li key={option.grade.key}>
                {header && <p className="eyebrow px-3 pb-0.5 pt-2">{header}</p>}
                <button
                  type="button"
                  // Keeps focus on the input, so the container's blur never
                  // closes the list out from under the click.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(option)}
                  onMouseEnter={() => setActive(i)}
                  className={`flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left text-sm ${
                    i === active ? "bg-surface-strong text-white" : "text-muted"
                  }`}
                >
                  <span>{option.grade.label}</span>
                  {/* Blank, not "—": a grade SportsCardsPro carries no figure for
                      is common, and a column of dashes reads as an error. */}
                  <span className={priceText ? "text-accent" : "text-faint"}>{priceText}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
